/**
 * <product-reel> — a product gallery video that plays while it is on screen and
 * loops, with nothing to press.
 *
 * The <video> deliberately has no autoplay attribute and preload="none". The
 * gallery prints its media twice (a slideshow for phones, a grid for desktop) and
 * hides one of them in CSS; with autoplay both copies would fetch the file. Here
 * the hidden copy never intersects the viewport, so it never loads.
 *
 * No imports: this has to run on its own wherever the gallery is re-rendered.
 */
class ProductReel extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const video = this.querySelector('video');

    if (!(video instanceof HTMLVideoElement)) return;

    // Without IntersectionObserver, fall back to playing straight away rather
    // than showing a still that never moves.
    if (!('IntersectionObserver' in window)) {
      video.play().catch(() => {});
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLVideoElement)) continue;

          if (entry.isIntersecting) {
            // Rejects when the browser refuses to autoplay; the poster stays up,
            // which is the right outcome, so there is nothing to handle.
            entry.target.play().catch(() => {});
          } else {
            entry.target.pause();
          }
        }
      },
      { threshold: 0.25 }
    );

    this.#observer.observe(video);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }
}

if (!customElements.get('product-reel')) {
  customElements.define('product-reel', ProductReel);
}
